import React, { useEffect } from "react";
import { Table, Typography, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import styled from "styled-components";
import UseApi from "../../../Reusable/CallApi/UseApi";
import { useSelector } from "react-redux";

const { Title } = Typography;
const PageWrapper = styled.div`
  // padding: 32px 40px;
  background: #ffffff;
  min-height: 100vh;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 16px;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledTitle = styled(Title)`
  &.ant-typography {
    margin: 0;
    color: #1f1f1f;
    font-weight: 600;
  }
`;

const TotalCount = styled.span`
  color: #595959;
  font-size: 14px;
`;

const SearchInput = styled(Input)`
  width: 260px;
  border-radius: 20px;
  padding: 8px 16px;
  background: #ffffff;
  border: 1px solid #d9d9d9;

  &:hover,
  &:focus,
  &.ant-input-affix-wrapper-focused {
    border-color: #bfbfbf;
  }

  .ant-input {
    background: transparent;
  }
`;

const TableCard = styled.div`
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #f0f0f0;
`;

const StyledTable = styled(Table)`
  .ant-table {
    background: #ffffff;
  }

  .ant-table-thead > tr > th {
    background: #ffffff;
    color: #1f1f1f;
    font-weight: 600;
    border-bottom: 1px solid #f0f0f0;
    padding: 18px 24px;
  }

  .ant-table-tbody > tr > td {
    padding: 18px 24px;
    color: #595959;
    border-bottom: none;
  }

  .ant-table-tbody > tr:nth-child(odd) > td {
    background: #ffffff;
  }

  .ant-table-tbody > tr:nth-child(even) > td {
    background: #fafafa;
  }

  .ant-table-tbody > tr:hover > td {
    background: #f5f5f5 !important;
  }

  .ant-pagination {
    padding: 16px 24px;
  }
`;

const PeopleReport = () => {
   const clientId = useSelector(
      (state) => state.authTracking.userInfo.clientId
    );
  const { data, loading, request } = UseApi();
  const [searchTerm, setSearchTerm] = React.useState("");

  useEffect(() => {
    // UseApi's request() rethrows on failure - uncaught here, this was a
    // genuine unhandled promise rejection (CRA's dev error overlay
    // whenever this endpoint fails).
    request({ endPoint: `/report/getcoursereport/${clientId}`, method: "get" }).catch(() => {});
  }, [clientId]);

  const allCourses = data?.response || [];
  const courses = allCourses.filter((item) =>
    Object.values(item).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );
  const metadataValue = (...keys) => (_, record) => {
    const value = keys
      .map((key) => record[key])
      .find((item) => item !== undefined && item !== null && item !== "");
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return value ?? "—";
  };

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Course Name",
      dataIndex: "courseName",
      key: "courseName",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
    },
    {
      title: "Enrolled in",
      dataIndex: "enrolledIn",
      key: "enrolledIn",
    },
    {
      title: "Completed",
      dataIndex: "completed",
      key: "completed",
    },
    {
      title: "In Progress",
      dataIndex: "inProgress",
      key: "inProgress",
    },
    {
      title: "Not Passed",
      dataIndex: "notPassed",
      key: "notPassed",
    },
    {
      title: "Available for",
      dataIndex: "grade_attend",
      key: "grade_attend",
      render: metadataValue("grade_attend", "gradeAttend"),
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      render: metadataValue("country"),
    },
    {
      title: "Hours",
      dataIndex: "hours",
      key: "hours",
      render: metadataValue("hours"),
    },
    {
      title: "Access",
      dataIndex: "access",
      key: "access",
      render: metadataValue("access"),
    },
    {
      title: "CPD Certified",
      dataIndex: "cpd_certified",
      key: "cpd_certified",
      render: metadataValue("cpd_certified", "cpdCertified"),
    },
    {
      title: "Certificate Level",
      dataIndex: "certificate_level",
      key: "certificate_level",
      render: metadataValue("certificate_level", "certificateLevel"),
    },
    {
      title: "CPD Hours",
      dataIndex: "cpd_hours",
      key: "cpd_hours",
      render: metadataValue("cpd_hours", "cpdHours"),
    },
  ];

  return (
    <PageWrapper>
      <HeaderRow>
        <TitleBlock>
          <StyledTitle level={3}>Course Report</StyledTitle>
          <TotalCount>Total Course: {allCourses.length}</TotalCount>
        </TitleBlock>
        <SearchInput
          placeholder="Search"
          prefix={<SearchOutlined />}
          allowClear
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </HeaderRow>

      <TableCard>
        <StyledTable
          columns={columns}
          dataSource={courses}
          loading={loading}
          rowKey={(record, index) => record.code || index}
          pagination={{ pageSize: 10 }}
          scroll={{ x: "max-content" }}
        />
      </TableCard>
    </PageWrapper>
  );
};

export default PeopleReport;